/**
 * EnforceOrganization Component
 *
 * Ensures that authenticated users have an organization membership.
 * If user has no organization, redirects to organization selection/creation flow.
 * Enforces organization-scoped routing for all protected routes.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery } from "convex/react";
import type { Route } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { SubscriptionRequiredDialog } from "@/components/billing/subscription-required-dialog";
import { PageWrapperLoading } from "@/components/page-wrapper";
import { buildOrganizationPath, getRelativeOrganizationPath, isPathWithinOrganization } from "@/lib/organization-path";

// Thrown errors (e.g. the auth context still settling right after org creation) can be
// transient, so retry a bounded number of times with exponential backoff before giving up.
// Deterministic `success: false` results are never retried.
const MAX_FREE_SUBSCRIPTION_ATTEMPTS = 3;
const FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS = 500;
interface EnforceOrganizationProps {
  children: ReactNode;
}
export function EnforceOrganization(t0) {
  const $ = _c(69);
  const {
    children
  } = t0;
  const {
    isLoaded: isClerkLoaded,
    isSignedIn
  } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const organizationStatus = useQuery(api.check_membership.hasOrganization);
  const ensureFreeSubscription = useMutation(api.payments.mutations.ensureFreeSubscriptionForActiveOrganization);
  const [retryVersion, setRetryVersion] = useState(0);
  const [freeSubscriptionFailedSlug, setFreeSubscriptionFailedSlug] = useState(null);
  const freeSubscriptionAttemptSlugRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      slug: null,
      attempts: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const freeSubscriptionRetryRef = useRef(t1);
  const freeSubscriptionRetryTimerRef = useRef(null);
  let t2;
  if ($[1] !== pathname) {
    t2 = pathname.startsWith("/onboarding");
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isOnboardingRoute = t2;
  const isAppRoute = pathname === "/app";
  const isAuthenticated = organizationStatus?.isAuthenticated ?? false;
  const isAuthBootstrapping = isClerkLoaded && isSignedIn && !isAuthenticated;
  const isLoading = !isClerkLoaded || organizationStatus === undefined || isAuthBootstrapping;
  const hasOrganization = organizationStatus?.hasOrganization ?? false;
  const activeOrganizationSlug = organizationStatus?.activeOrganizationSlug ?? null;
  const billingStatus = organizationStatus?.billingStatus ?? "none";
  const hasActiveSubscription = organizationStatus?.hasActiveSubscription ?? false;
  let t3;
  if ($[3] !== activeOrganizationSlug) {
    t3 = activeOrganizationSlug !== null ? buildOrganizationPath(activeOrganizationSlug, "/") : null;
    $[3] = activeOrganizationSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const organizationDashboardPath = t3;
  const isOrganizationDashboardRoute = organizationDashboardPath !== null && pathname === organizationDashboardPath;
  const isBillingRequiredRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/billing-required`;
  const isBillingSettingsRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/settings/billing`;
  const isUnpaidAllowedRoute = isOrganizationDashboardRoute || isBillingSettingsRoute;
  const isWaitingForFreeSubscription = isSignedIn && hasOrganization && activeOrganizationSlug !== null && !hasActiveSubscription && billingStatus === "none" && freeSubscriptionFailedSlug !== activeOrganizationSlug;
  let t4;
  if ($[5] !== activeOrganizationSlug || $[6] !== billingStatus || $[7] !== ensureFreeSubscription || $[8] !== freeSubscriptionFailedSlug || $[9] !== hasActiveSubscription || $[10] !== hasOrganization || $[11] !== isLoading || $[12] !== isSignedIn) {
    t4 = () => {
      if (isLoading || !isSignedIn || !hasOrganization || !activeOrganizationSlug || hasActiveSubscription || billingStatus !== "none" || freeSubscriptionAttemptSlugRef.current === activeOrganizationSlug || freeSubscriptionFailedSlug === activeOrganizationSlug) {
        return;
      }
      if (freeSubscriptionRetryTimerRef.current) {
        clearTimeout(freeSubscriptionRetryTimerRef.current);
        freeSubscriptionRetryTimerRef.current = null;
      }
      if (freeSubscriptionRetryRef.current.slug !== activeOrganizationSlug) {
        freeSubscriptionRetryRef.current = {
          slug: activeOrganizationSlug,
          attempts: 0
        };
      }
      freeSubscriptionAttemptSlugRef.current = activeOrganizationSlug;
      ensureFreeSubscription({}).then(result => {
        if (!result.success) {
          setFreeSubscriptionFailedSlug(activeOrganizationSlug);
        }
      }).catch(error => {
        console.error("Failed to ensure free subscription:", error);
        const attempts = freeSubscriptionRetryRef.current.attempts + 1;
        freeSubscriptionRetryRef.current = {
          slug: activeOrganizationSlug,
          attempts
        };
        if (attempts >= MAX_FREE_SUBSCRIPTION_ATTEMPTS) {
          setFreeSubscriptionFailedSlug(activeOrganizationSlug);
          return;
        }
        const delay = FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS * 2 ** (attempts - 1);
        freeSubscriptionRetryTimerRef.current = setTimeout(() => {
          freeSubscriptionAttemptSlugRef.current = null;
          setRetryVersion(_temp);
        }, delay);
      }).finally(() => {
        setRetryVersion(_temp2);
      });
    };
    $[5] = activeOrganizationSlug;
    $[6] = billingStatus;
    $[7] = ensureFreeSubscription;
    $[8] = freeSubscriptionFailedSlug;
    $[9] = hasActiveSubscription;
    $[10] = hasOrganization;
    $[11] = isLoading;
    $[12] = isSignedIn;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== activeOrganizationSlug || $[15] !== billingStatus || $[16] !== ensureFreeSubscription || $[17] !== freeSubscriptionFailedSlug || $[18] !== hasActiveSubscription || $[19] !== hasOrganization || $[20] !== isLoading || $[21] !== isSignedIn || $[22] !== retryVersion) {
    t5 = [isLoading, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, billingStatus, freeSubscriptionFailedSlug, ensureFreeSubscription, retryVersion];
    $[14] = activeOrganizationSlug;
    $[15] = billingStatus;
    $[16] = ensureFreeSubscription;
    $[17] = freeSubscriptionFailedSlug;
    $[18] = hasActiveSubscription;
    $[19] = hasOrganization;
    $[20] = isLoading;
    $[21] = isSignedIn;
    $[22] = retryVersion;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (freeSubscriptionRetryTimerRef.current) {
        clearTimeout(freeSubscriptionRetryTimerRef.current);
      }
    };
    t7 = [];
    $[24] = t6;
    $[25] = t7;
  } else {
    t6 = $[24];
    t7 = $[25];
  }
  useEffect(t6, t7);
  let t8;
  if ($[26] !== activeOrganizationSlug || $[27] !== hasActiveSubscription || $[28] !== hasOrganization || $[29] !== isAppRoute || $[30] !== isBillingRequiredRoute || $[31] !== isLoading || $[32] !== isOnboardingRoute || $[33] !== isSignedIn || $[34] !== isUnpaidAllowedRoute || $[35] !== isWaitingForFreeSubscription || $[36] !== pathname || $[37] !== router || $[38] !== searchParams) {
    t8 = () => {
      if (isLoading || isWaitingForFreeSubscription) {
        return;
      }
      if (!isSignedIn) {
        router.replace("/sign-in");
        return;
      }
      if (!hasOrganization || !activeOrganizationSlug) {
        if (!isOnboardingRoute && !isAppRoute) {
          router.replace("/onboarding/choose-organization");
        }
        return;
      }
      if (!hasActiveSubscription && isBillingRequiredRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (hasActiveSubscription && isBillingRequiredRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
        return;
      }
      if (isOnboardingRoute) {
        const returnTo = searchParams.get("returnTo");
        const target = returnTo ? buildOrganizationPath(activeOrganizationSlug, returnTo) : buildOrganizationPath(activeOrganizationSlug, "/");
        router.replace(target as Route);
        return;
      }
      const isWithinOrg = isPathWithinOrganization(activeOrganizationSlug, pathname);
      if (!isWithinOrg) {
        const redirectPath = buildOrganizationPath(activeOrganizationSlug, getRelativeOrganizationPath(activeOrganizationSlug, pathname));
        router.replace(redirectPath as Route);
      }
    };
    $[26] = activeOrganizationSlug;
    $[27] = hasActiveSubscription;
    $[28] = hasOrganization;
    $[29] = isAppRoute;
    $[30] = isBillingRequiredRoute;
    $[31] = isLoading;
    $[32] = isOnboardingRoute;
    $[33] = isSignedIn;
    $[34] = isUnpaidAllowedRoute;
    $[35] = isWaitingForFreeSubscription;
    $[36] = pathname;
    $[37] = router;
    $[38] = searchParams;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== activeOrganizationSlug || $[41] !== hasActiveSubscription || $[42] !== hasOrganization || $[43] !== isAppRoute || $[44] !== isAuthenticated || $[45] !== isBillingRequiredRoute || $[46] !== isLoading || $[47] !== isOnboardingRoute || $[48] !== isSignedIn || $[49] !== isUnpaidAllowedRoute || $[50] !== isWaitingForFreeSubscription || $[51] !== pathname || $[52] !== router || $[53] !== searchParams) {
    t9 = [isLoading, isAuthenticated, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, isWaitingForFreeSubscription, isBillingRequiredRoute, isUnpaidAllowedRoute, isOnboardingRoute, isAppRoute, pathname, router, searchParams];
    $[40] = activeOrganizationSlug;
    $[41] = hasActiveSubscription;
    $[42] = hasOrganization;
    $[43] = isAppRoute;
    $[44] = isAuthenticated;
    $[45] = isBillingRequiredRoute;
    $[46] = isLoading;
    $[47] = isOnboardingRoute;
    $[48] = isSignedIn;
    $[49] = isUnpaidAllowedRoute;
    $[50] = isWaitingForFreeSubscription;
    $[51] = pathname;
    $[52] = router;
    $[53] = searchParams;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  useEffect(t8, t9);
  if (isLoading) {
    let t10;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[55] = t10;
    } else {
      t10 = $[55];
    }
    return t10;
  }
  if (isWaitingForFreeSubscription) {
    let t10;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[56] = t10;
    } else {
      t10 = $[56];
    }
    return t10;
  }
  if (!isSignedIn) {
    let t10;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[57] = t10;
    } else {
      t10 = $[57];
    }
    return t10;
  }
  if (!hasOrganization || !activeOrganizationSlug) {
    if (isOnboardingRoute || isAppRoute) {
      return children;
    }
    let t10;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[58] = t10;
    } else {
      t10 = $[58];
    }
    return t10;
  }
  if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
    return children;
  }
  if (isOnboardingRoute) {
    let t10;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[59] = t10;
    } else {
      t10 = $[59];
    }
    return t10;
  }
  if (!hasActiveSubscription && isBillingRequiredRoute) {
    let t10;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[60] = t10;
    } else {
      t10 = $[60];
    }
    return t10;
  }
  if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
    let t10;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[61] = t10;
    } else {
      t10 = $[61];
    }
    return t10;
  }
  const isWithinOrg_0 = isPathWithinOrganization(activeOrganizationSlug, pathname);
  if (!isWithinOrg_0) {
    let t10;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PageWrapperLoading />;
      $[62] = t10;
    } else {
      t10 = $[62];
    }
    return t10;
  }
  let t10;
  if ($[63] !== hasActiveSubscription || $[64] !== isOrganizationDashboardRoute) {
    t10 = !hasActiveSubscription && isOrganizationDashboardRoute ? <SubscriptionRequiredDialog /> : null;
    $[63] = hasActiveSubscription;
    $[64] = isOrganizationDashboardRoute;
    $[65] = t10;
  } else {
    t10 = $[65];
  }
  let t11;
  if ($[66] !== children || $[67] !== t10) {
    t11 = <>{children}{t10}</>;
    $[66] = children;
    $[67] = t10;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  return t11;
}
function _temp2(version_0) {
  return version_0 + 1;
}
function _temp(version) {
  return version + 1;
}